import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { I18n, T } from '@deity/falcon-i18n';
import withStoreConfiguration from '../../helpers/StoreConfiguration/StoreConfiguration';
import { Preloader } from '../../ui/Preloader/Preloader';
import Container from '../../layout/Container/Container';
import { Breadcrumbs } from '../../elements/Breadcrumbs/Breadcrumbs';
import { PrefixForm } from '../../elements/SocialConnect/AppleId/PrefixForm';
import { Button } from '../../ui/Button/Button';
import {
  useSocialLoginMutation,
  REGISTRATION_SUCCESS,
  REGISTRATION_MISSING_PREFIX,
  REGISTRATION_FAILED
} from '../../elements/SocialConnect/AppleId/LoginWithSocialLoginMutation';
import { SocialLoginQuery } from '../../elements/SocialConnect/AppleId/LoginWithSocialLoginQuery';
import { LoginTracking } from '../../elements/Tracking/GoogleTagManager';
import styles from './CustomerLogin.module.scss';

const SocialConnectLogin = withRouter(
  withStoreConfiguration(({ storeConfiguration, history }) => {
    const [result, setResult] = useState(null);
    const urlParams = new URLSearchParams(window.location.search);
    const hauthDone = urlParams.get('hauth_done') || urlParams.get('hauth.done');
    const type = urlParams.get('type') || hauthDone?.toLowerCase();
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const idToken = urlParams.get('id_token');
    const enabled = storeConfiguration?.amsociallogin?.[type]?.enabled || null;
    const translatePrefix = `loginWith${type?.[0]?.toUpperCase() + type?.slice(1)?.toLowerCase()}`;
    // this is how amazon answers on error
    const paramsError =
      urlParams.get('error') && urlParams.get('error_description')
        ? `${urlParams.get('error')} - ${urlParams.get('error_description')}`
        : '';

    const onCompleted = ({ socialConnectLogin }) => {
      setResult(socialConnectLogin);
      if (socialConnectLogin?.status === REGISTRATION_SUCCESS) {
        LoginTracking({ storeConfiguration, loginMethod: type });
        setTimeout(() => {
          history.push('/account');
        }, 2000);
      }
    };

    const [socialLoginMutation, { loading }] = useSocialLoginMutation({
      onCompleted
    });

    // handle callback from partner website
    useEffect(() => {
      if (code) {
        socialLoginMutation({
          variables: {
            type,
            code,
            idToken
          }
        });
      }
    }, []);

    if (enabled === '0' || !enabled || typeof window === 'undefined') {
      return <div>${translatePrefix} login is disabled.</div>;
    }

    if (type === null || type === '') {
      return <div>Error: no socialConnect Type</div>;
    }

    // redirect to partner website
    if (!code && !error) {
      return (
        <SocialLoginQuery variables={{ type }}>
          {({ data: { socialConnectLink }, loading: socialLoginLoading }) => {
            if (socialLoginLoading) {
              return <Preloader />;
            }

            if (socialConnectLink?.redirectLink) {
              window.location.href = socialConnectLink.redirectLink;
              return <Preloader />;
            }
          }}
        </SocialLoginQuery>
      );
    }

    return (
      <I18n>
        {t => (
          <Container>
            <Breadcrumbs breadcrumbs={[{ name: t(`${translatePrefix}.title`), urlPath: '#' }]} />
            <div className={styles.LoginFrame}>
              <h1>
                <T id={`${translatePrefix}.title`} />
              </h1>
              {loading ? (
                <Preloader />
              ) : (
                <React.Fragment>
                  {result?.status ? (
                    <React.Fragment>
                      {result?.status === REGISTRATION_SUCCESS ? (
                        <div className={styles.Success}>
                          <p>
                            <T id="signUp.success" />
                          </p>
                        </div>
                      ) : null}
                      {result?.status === REGISTRATION_MISSING_PREFIX ? (
                        <PrefixForm type={type} code={code} idToken={idToken} onSuccess={onCompleted} />
                      ) : null}
                      {result?.status === REGISTRATION_FAILED ? (
                        <>
                          <div className={styles.Error}>
                            <T id={result?.message} />
                          </div>
                          <LoginTracking
                            storeConfiguration={storeConfiguration}
                            failed
                            errorMessage={result?.message}
                            loginMethod={type}
                          />
                        </>
                      ) : null}
                    </React.Fragment>
                  ) : (
                    <>
                      <div className={styles.Error}>{result?.message}</div>
                      {paramsError || result?.message ? (
                        <LoginTracking
                          storeConfiguration={storeConfiguration}
                          failed
                          errorMessage={paramsError || result?.message}
                          loginMethod={type}
                        />
                      ) : null}
                    </>
                  )}

                  {!result ? (
                    <>
                      {error ? (
                        <>
                          <div className={styles.Error}>
                            <T id={`${translatePrefix}.${error}`} />
                          </div>
                          <LoginTracking
                            storeConfiguration={storeConfiguration}
                            failed
                            errorMessage={t(`${translatePrefix}.${error}`)}
                            loginMethod={type}
                          />
                        </>
                      ) : (
                        <div className={styles.Error}>
                          <T id={`${translatePrefix}.general`} />
                        </div>
                      )}

                      <div className={styles.ErrorButtonWrapper}>
                        <Button asLink path="/sign-in">
                          <T id="signIn.buttonBack" />
                        </Button>
                      </div>
                    </>
                  ) : null}
                </React.Fragment>
              )}
            </div>
          </Container>
        )}
      </I18n>
    );
  })
);

export default SocialConnectLogin;
